/**
 * TargetGroup Component
 *
 * Defines a group of targets for camera tracking.
 * The camera will frame all targets, optionally with auto-zoom.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  field,
  customEditor,
} from "../attributes";
import type { Level } from "../Level";

/** Target rotation mode for group */
export enum GroupRotationMode {
  /** Manual control */
  Manual = 0,
  /** Face group center */
  GroupAverage = 1,
  /** Face position of first target */
  FirstTarget = 2,
}

/**
 * Individual target in a group
 */
export interface TargetGroupMember {
  /** GUID of the target GameObject */
  target: string;
  /** Weight for position averaging (0-1) */
  weight: number;
  /** Radius around target that must be visible */
  radius: number;
}

@component("core.TargetGroup")
@customEditor("TargetGroupEditor")
@displayName("Target Group")
@icon("Users")
@color("#f97316") // Orange
@category("Camera")
export class TargetGroup {
  /** List of targets with weights */
  @field({ type: "string", label: "Targets (JSON)", tooltip: "JSON array of {target, weight, radius}" })
  targetsJson: string = "[]";

  /** Minimum size (ortho size / zoom) */
  @field({ type: "f32", label: "Min Size", min: 0.5, max: 100, step: 0.1 })
  minSize: number = 3;

  /** Maximum size (ortho size / zoom) */
  @field({ type: "f32", label: "Max Size", min: 1, max: 200, step: 0.1 })
  maxSize: number = 20;

  /** Padding around the group (world units) */
  @field({ type: "f32", label: "Padding", min: 0, max: 20, step: 0.1 })
  padding: number = 2;

  /** How fast to adjust zoom */
  @field({ type: "f32", label: "Zoom Damping", min: 0, max: 5, step: 0.1 })
  zoomDamping: number = 0.5;

  /** Position damping for group center */
  @field({ type: "f32", label: "Position Damping", min: 0, max: 5, step: 0.1 })
  positionDamping: number = 0.3;

  /** Rotation mode */
  @field({ type: "i32", label: "Rotation Mode", tooltip: "0=Manual, 1=GroupAverage, 2=FirstTarget" })
  rotationMode: number = GroupRotationMode.Manual;
}

/**
 * Parse targets from JSON string
 */
export function parseTargetGroupMembers(json: string): TargetGroupMember[] {
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr.map(item => ({
      target: String(item.target ?? ""),
      weight: Math.max(0, Math.min(1, Number(item.weight ?? 1))),
      radius: Math.max(0, Number(item.radius ?? 0)),
    }));
  } catch {
    return [];
  }
}

/**
 * Serialize targets to JSON string
 */
export function serializeTargetGroupMembers(members: TargetGroupMember[]): string {
  return JSON.stringify(members);
}

/**
 * Calculate the weighted center and bounding box of the target group
 */
export function evaluateTargetGroup(
  resolvePos: (guid: string) => { x: number; y: number } | null,
  groupData: TargetGroup,
  aspectRatio: number = 16 / 9
): {
  center: { x: number; y: number };
  suggestedSize: number;
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
  validTargets: number;
} | null {
  const targetsJson = groupData.targetsJson ?? "[]";
  const minSize = groupData.minSize ?? 3;
  const maxSize = groupData.maxSize ?? 20;
  const padding = groupData.padding ?? 2;

  const members = parseTargetGroupMembers(targetsJson);
  if (members.length === 0) return null;

  // Resolve targets and calculate weighted center + bounds
  let totalWeight = 0;
  let centerX = 0;
  let centerY = 0;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let validTargets = 0;

  for (const member of members) {
    if (!member.target) continue;

    const pos = resolvePos(member.target);
    if (!pos) continue;

    const weight = member.weight;
    const radius = member.radius;

    totalWeight += weight;
    centerX += pos.x * weight;
    centerY += pos.y * weight;

    // Expand bounds by radius
    minX = Math.min(minX, pos.x - radius);
    maxX = Math.max(maxX, pos.x + radius);
    minY = Math.min(minY, pos.y - radius);
    maxY = Math.max(maxY, pos.y + radius);
    
    validTargets++;
  }

  if (validTargets === 0 || totalWeight === 0) return null;

  // Normalize weighted center
  centerX /= totalWeight;
  centerY /= totalWeight;

  // Add padding to bounds
  minX -= padding;
  maxX += padding;
  minY -= padding;
  maxY += padding;

  // Calculate required ortho size to fit all targets
  const boundsWidth = maxX - minX;
  const boundsHeight = maxY - minY;

  // OrthoSize is half the vertical extent
  // For width, we need to account for aspect ratio
  const sizeForHeight = boundsHeight / 2;
  const sizeForWidth = (boundsWidth / 2) / aspectRatio;

  // Take the larger to ensure everything fits
  let suggestedSize = Math.max(sizeForHeight, sizeForWidth);
  
  // Clamp to min/max
  suggestedSize = Math.max(minSize, Math.min(maxSize, suggestedSize));

  return {
    center: { x: centerX, y: centerY },
    suggestedSize,
    bounds: { minX, maxX, minY, maxY },
    validTargets,
  };
}
